import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import {
  expandCombineSelectionToSourcePolicies,
  listCombinablePolicyNodes,
  MIN_COMBINED_POLICY_SOURCES,
  strategyIdForCombineCandidate,
} from "./workspaceCombinedPolicyUtils";

function isCombineSelectable(node: WorkspaceNode): boolean {
  return (
    (node.type === "policy" || node.type === "combined_policy") && !node.isDisabled
  );
}

/**
 * Toggle a policy / combined_policy into an ordered multi-select for Combine.
 * Only same-strategy combinable nodes accumulate; anything else resets to that node alone.
 */
export function nextPolicyCombineSelection(
  clicked: WorkspaceNode,
  currentIds: string[],
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): string[] {
  if (!isCombineSelectable(clicked)) {
    return [clicked.id];
  }

  const currentNodes = currentIds
    .map((id) => nodes.find((n) => n.id === id))
    .filter((n): n is WorkspaceNode => Boolean(n && isCombineSelectable(n)));

  if (currentNodes.length === 0) {
    return [clicked.id];
  }

  const anchor = currentNodes[0]!;
  const combinableIds = new Set(
    listCombinablePolicyNodes(anchor, nodes, edges).map((n) => n.id)
  );
  if (!combinableIds.has(clicked.id)) {
    return [clicked.id];
  }

  const anchorStrategy = strategyIdForCombineCandidate(anchor, nodes, edges);
  const clickedStrategy = strategyIdForCombineCandidate(clicked, nodes, edges);
  if (anchorStrategy && clickedStrategy && anchorStrategy !== clickedStrategy) {
    return [clicked.id];
  }

  if (currentIds.includes(clicked.id)) {
    const next = currentIds.filter((id) => id !== clicked.id);
    return next.length > 0 ? next : [clicked.id];
  }

  return [...currentIds, clicked.id];
}

export function canOfferCombinePolicies(
  selectedIds: string[],
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[] = []
): boolean {
  if (selectedIds.length < 2) return false;
  const selected = selectedIds
    .map((id) => nodes.find((n) => n.id === id))
    .filter((n): n is WorkspaceNode => Boolean(n && isCombineSelectable(n)));
  if (selected.length < 2) return false;
  const expanded = expandCombineSelectionToSourcePolicies(selected, nodes, edges);
  return expanded.length >= MIN_COMBINED_POLICY_SOURCES;
}

/** Combinable policy / combined_policy ids for the current multi-select anchor. */
export function combinablePolicyIdsForSelection(
  selectedIds: string[],
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Set<string> | null {
  const first = selectedIds[0] ? nodes.find((n) => n.id === selectedIds[0]) : null;
  if (!first || !isCombineSelectable(first)) return null;
  return new Set(listCombinablePolicyNodes(first, nodes, edges).map((n) => n.id));
}
